import type { ReactNode } from 'react'
import { AlertTriangle, CircleDot, Database, ShieldCheck, Workflow } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DeliveryStatus, EvaluationResult, Trigger, TriggerSpec, TriggerStatus } from './types'
import { fill } from './types'
import { describeSource, sourceKindLabel, thingWord, triggerSentence } from './builder'

type Copy = Record<string, string>

const STATUS_TONE: Record<TriggerStatus, string> = {
  active: 'bg-emerald-500',
  paused: 'bg-muted-foreground/50',
  draft: 'bg-sky-500',
  suspended: 'bg-destructive',
}

export function StatusPill({ status, copy }: { status: TriggerStatus; copy: Copy }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2 py-0.5 text-xs font-medium">
      <span className={cn('size-1.5 rounded-full', STATUS_TONE[status])} aria-hidden />
      {copy[`status_${status}`]}
    </span>
  )
}

const RESULT_TONE: Record<EvaluationResult | 'never', string> = {
  fired: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300',
  quiet: 'text-muted-foreground bg-muted/60',
  skipped: 'text-muted-foreground bg-muted/60',
  held: 'text-amber-800 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-200',
  failed: 'text-destructive bg-destructive/10',
  running: 'text-sky-700 bg-sky-50 dark:bg-sky-950/40 dark:text-sky-300',
  never: 'text-muted-foreground bg-muted/60',
}

export function ResultChip({ result, copy }: { result?: EvaluationResult | null; copy: Copy }) {
  const key = result ?? 'never'
  return <span className={cn('inline-flex rounded-md px-1.5 py-0.5 text-xs font-medium', RESULT_TONE[key])}>{copy[`result_${key}`]}</span>
}

const DELIVERY_TONE: Record<DeliveryStatus, string> = {
  pending: 'text-sky-700 bg-sky-50 dark:bg-sky-950/40 dark:text-sky-300',
  delivering: 'text-sky-700 bg-sky-50 dark:bg-sky-950/40 dark:text-sky-300',
  delivered: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300',
  recorded: 'text-muted-foreground bg-muted/60',
  failed: 'text-amber-800 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-200',
  dead: 'text-destructive bg-destructive/10',
}

export function DeliveryChip({ status, copy }: { status: DeliveryStatus; copy: Copy }) {
  return <span className={cn('inline-flex rounded-md px-1.5 py-0.5 text-xs font-medium', DELIVERY_TONE[status])}>{copy[`delivery_${status}`]}</span>
}

export function Banner({ tone, children }: { tone: 'warning' | 'error' | 'info'; children: ReactNode }) {
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={cn(
      'flex items-start gap-2 rounded-xl border px-4 py-3 text-sm',
      tone === 'info' && 'border-border bg-muted/30',
      tone === 'warning' && 'border-amber-500/40 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100',
      tone === 'error' && 'border-destructive/40 bg-destructive/5 text-destructive',
    )}>
      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  )
}

export function targetLabel(spec: TriggerSpec, copy: Copy): string {
  return spec.target.kind === 'praxis' ? `Praxis · ${spec.target.workflow}` : copy.record_only
}

function Block({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="grid content-start gap-3 rounded-xl border border-border bg-background p-4">
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">{icon}{title}</h3>
      <div className="grid gap-2 text-sm">{children}</div>
    </section>
  )
}

export function SpecBlocks({ spec, copy, workflowsPath }: { spec: TriggerSpec; copy: Copy; workflowsPath?: string | null }) {
  const gate = spec.gate ?? {}
  const source = spec.source
  const staticInput = spec.target.kind === 'praxis' ? spec.target.input?.static : undefined
  const code = source.kind === 'python' ? source.code : source.kind === 'sql' ? source.sql : null
  return (
    <div className="grid gap-3 @3xl:grid-cols-3">
      <Block icon={<Database className="size-3.5" aria-hidden />} title={copy.when}>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{sourceKindLabel(source, copy)}</p>
        <p className="font-medium">{describeSource(source, copy)}</p>
        <p className="text-muted-foreground">{triggerSentence(spec, copy)}</p>
        {source.kind === 'table' && (
          <p className="text-xs text-muted-foreground">{copy.identity_label}: {source.identity.map((k) => source.fields.find((f) => f.key === k)?.label || k).join(' + ')} · <span className="font-mono">{source.table}</span></p>
        )}
        {spec.entity.length > 0 && (source.kind === 'python' || source.kind === 'sql') && (
          <p className="text-xs text-muted-foreground">{copy.identity_label}: <span className="font-mono">{spec.entity.join(' + ')}</span></p>
        )}
        {code && (
          <details className="group">
            <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">{source.kind === 'python' ? copy.see_code : copy.see_query}</summary>
            <pre className="mt-2 max-h-56 overflow-auto rounded-lg bg-muted/50 p-3 font-mono text-[11px] leading-relaxed">{code}</pre>
          </details>
        )}
        <p className="text-xs text-muted-foreground">{fill(copy.check_every, { minutes: spec.schedule?.check_every_minutes ?? 15 })}</p>
      </Block>
      <Block icon={<Workflow className="size-3.5" aria-hidden />} title={copy.then}>
        <p className="font-medium">
          {spec.target.kind === 'praxis' && workflowsPath
            ? <a href={workflowsPath} className="underline-offset-2 hover:underline">{targetLabel(spec, copy)}</a>
            : targetLabel(spec, copy)}
        </p>
        {spec.target.kind === 'praxis' && (
          <p className="text-muted-foreground">{fill(spec.target.batch ? copy.batch_all : copy.batch_one, { thing: thingWord(source, copy) })}</p>
        )}
        <p className="text-muted-foreground">{spec.start === 'all' ? copy.activation_all : copy.activation_now}</p>
        {staticInput && Object.keys(staticInput).length > 0 && (
          <div className="text-xs">
            <span className="text-muted-foreground">{copy.extra_label}: </span>
            <code className="font-mono">{JSON.stringify(staticInput)}</code>
          </div>
        )}
      </Block>
      <Block icon={<ShieldCheck className="size-3.5" aria-hidden />} title={copy.protections}>
        <ul className="grid gap-1.5 text-muted-foreground">
          {gate.require_sync_success !== false && <Line>{copy.gate_sync}</Line>}
          {gate.allow_partial_sync && <Line>{copy.gate_partial}</Line>}
          <Line>{fill(copy.gate_staleness, { hours: gate.max_staleness_hours ?? 36 })}</Line>
          <Line>{fill(copy.gate_exit, { share: `${Math.round((gate.max_exit_share ?? 0.8) * 100)}%` })}</Line>
          <Line>{fill(copy.gate_events, { events: gate.max_events ?? 200 })}</Line>
          {gate.min_rows != null && <Line>{fill(copy.gate_min_rows, { rows: gate.min_rows })}</Line>}
        </ul>
      </Block>
    </div>
  )
}

function Line({ children }: { children: ReactNode }) {
  return <li className="flex items-start gap-2"><CircleDot className="mt-1 size-3 shrink-0 text-emerald-600" aria-hidden /><span>{children}</span></li>
}

/** The one thing a person should know about a trigger right now, if any. */
export function attentionText(trigger: Trigger, copy: Copy): { tone: 'warning' | 'error'; text: string } | null {
  if (trigger.status === 'suspended') return { tone: 'error', text: fill(copy.suspended_banner, { reason: trigger.suspended_reason ?? '' }) }
  if ((trigger.events_dead ?? 0) > 0) return { tone: 'error', text: fill(copy.dead_banner, { count: trigger.events_dead ?? 0 }) }
  if (trigger.status === 'active' && trigger.last_status === 'failed') return { tone: 'error', text: trigger.last_error ?? copy.result_failed }
  if (trigger.status === 'active' && trigger.last_status === 'held') {
    return { tone: 'warning', text: fill(copy.held_banner, { reason: trigger.last_gate?.reasons?.[0] ?? '' }) }
  }
  return null
}
